import { useDeviceQuery } from '@almbrand/usedevicequery';
import { findIconUrl } from 'common/PolicyUtil';
import { DownloadTracking } from 'common/trackingUtils';
import { MultiColItemCard } from 'components/2-molecules/MultiColItemCard';
import { PreviewModal } from 'components/2-molecules/PreviewModal/PreviewModal';
import { HttpStatusCode, ThemeNames } from 'constantsValues';
import usePolicyPreview from 'hooks/usePolicyPreview';
import { useThemeName } from 'hooks/useThemeName';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from 'store/hooks';
import { getStatusMessage } from 'utilities/statusMessageUtils';
import './PolicyComponent.scss';

export interface PolicyProps {
	mainArea: {
		items: Array<{
			content: {
				title: string;
				icon: {
					url: string;
					properties: {
						altText?: string;
					};
				};
			};
		}>;
	};

	statusMessages?: {
		items: Array<{
			content: {
				statusMessage: string;
				statusCode: HttpStatusCode;
			};
		}>;
	};

	title: string;
}

export const PolicyComponent: React.FC<PolicyProps> = ({ mainArea, title, statusMessages }) => {
	const navigate = useNavigate();
	const policies = useAppSelector((state) => state.policies.policies);
	const status = useAppSelector((state) => state.policies.status);
	const partyStatus = useAppSelector((state) => state.party.status);
	const themeName = useThemeName();
	const isAlmbrand = themeName === ThemeNames.almbrand;

	const { isMobile, isTablet } = useDeviceQuery();
	const trackingThemeName = useThemeName();

	const { openModal, previewDocument, handlePolicyClick, handlePolicyDownload, onModalClose } = usePolicyPreview();

	// Map policies to InsuranceList props format
	const mappedPolicies = policies?.map((policy) => ({
		id: policy.policy_number, // Use the policy number as the unique identifier
		icon: findIconUrl(policy.title, mainArea), // Dynamically fetch icon URL
		title: policy.title, // Use the title from policies
		subtitle: policy.subtitle, // Use the subtitle
		price: `${policy.price?.amount || 0} ${policy.price?.unit}/år`, // Format price
	}));

	// Navigate to all policies
	const handleViewAllClick = () => {
		navigate('/forsikringer');
	};

	const onPolicyClick = (id: string, title: string) => {
		const screenSize = isMobile || isTablet ? 'download' : 'preview';
		const downloadTracking = DownloadTracking('policy', 'pdf', screenSize, trackingThemeName);
		handlePolicyClick(id, title, downloadTracking);
	};

	if (partyStatus !== HttpStatusCode.OK) return null; // Don't render anything if party data is not OK

	return (
		<>
			<MultiColItemCard
				title={title} // Pass dynamic title prop
				items={mappedPolicies} // Pass the entire list of policies
				onMoreClick={handleViewAllClick} // Navigate to all policies
				moreClickDestination='/forsikringer'
				onItemClick={isAlmbrand ? (policy) => onPolicyClick(policy.id, policy.title) : undefined} // Pass the download callback
				showError={status === HttpStatusCode.InternalServerError || status === HttpStatusCode.NoContent}
				errorMessage={getStatusMessage(statusMessages, status) ?? 'Der er ingen tilgængelige data.'}
			/>

			{openModal && (
				<PreviewModal
					displayAsPdf={previewDocument.displayAsPdf}
					data={previewDocument.data}
					handleMessageDownload={handlePolicyDownload}
					onClose={onModalClose}
				/>
			)}
		</>
	);
};

export default PolicyComponent;
